import { Body } from "@hypercard/web-components";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { availableRoles, hasRole, roleState } from "@common/state/roleState";
import { useRecoilState, useRecoilValue } from "recoil";
import { User, UserRole } from "@common/utils/graphql";
import { useNavigate } from "react-router-dom";
import { CaretDown } from "@phosphor-icons/react";
import FeedbackDialog from "../FeedbackDialog";
import {
  impersonatableUserState,
  isImpersonatableState,
  isImpersonatingState,
} from "@common/state/impersonsonatingUser";
import { useApolloClient } from "@apollo/client";
import { useFeatureFlags } from "@common/hooks";
import { Badge } from "@/components/ui/badge";
import { needsAction } from "@common/state/organizationState";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useUserStore } from "@domains/auth/utils/useUserStore";
import { getInitialsFromDisplayName } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import posthog from "posthog-js";
import { useAuthStore } from "@domains/auth/utils";
import { AUTH_ROUTES } from "@domains/auth/routes";
import { useState } from "react";

export const TopNav = () => {
  const [role, setRole] = useRecoilState(roleState);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const isImpersonatable = useRecoilValue(isImpersonatableState);
  const isImpersonating = useRecoilValue(isImpersonatingState);
  const userStore = useUserStore.getState();
  const { displayName, workEmail } = userStore;
  const [impersonatableState, setImpersonatableState] = useRecoilState(
    impersonatableUserState,
  );
  const { isDelegateUsersEnabled } = useFeatureFlags();
  const client = useApolloClient();
  const navigate = useNavigate();
  const roles = useRecoilValue(availableRoles);
  const hasAdmin = hasRole(UserRole.Admin, roles);
  const hasManager = hasRole(UserRole.Manager, roles);
  const isActionRequired = useRecoilValue(needsAction);
  const isAdmin = role === UserRole.Admin;

  const onOpenSettings = () => {
    setIsUserMenuOpen(false);
    navigate(`/settings`);
  };

  const setImpersonatingUser = async (impersonatingUser: User | null) => {
    setImpersonatableState((prev) => ({
      ...prev,
      impersonatingUser,
    }));
    await client.clearStore();
    navigate("/"); // push to root b/c our router redirects it to home, which will reload the home page if they're already on it
  };

  const handleRoleChange = (e: string) => {
    if (e === "impersonate") {
      setImpersonatingUser(impersonatableState.impersonatableUsers[0]);
      setRole(UserRole.Employee); // ensure the delegete view is an employee view
    } else {
      setRole(e as UserRole);
      if (isImpersonating) {
        setImpersonatingUser(null);
      }
    }
  };

  const onLogout = () => {
    userStore.clear();
    posthog.reset();
    useAuthStore.getState().signOut();
    navigate(AUTH_ROUTES.SIGN_OUT.path);
  };

  const handleChangeImpersonatingUser = (userId: string) => {
    const impersonatingUser = impersonatableState.impersonatableUsers.find(
      (user) => user.userId === userId,
    );
    if (impersonatingUser) {
      setImpersonatingUser(impersonatingUser);
    }
  };

  const canImpersonateUser = isImpersonatable && isDelegateUsersEnabled;
  return (
    <div className="flex min-h-10 items-center justify-between">
      <div className="flex h-10 items-center overflow-hidden">
        <DotLottieReact
          src="/hypercard-logo.lottie"
          autoplay
          className="relative -left-6 h-[7.75rem]"
        />
      </div>
      <div className="flex  space-x-4">
        <FeedbackDialog />
        {isImpersonating && (
          <Select
            value={impersonatableState.impersonatingUser?.userId || ""}
            onValueChange={handleChangeImpersonatingUser}
          >
            <SelectTrigger className="bg-background">
              <SelectValue asChild>
                <Body size="sm" weight="regular">
                  {impersonatableState.impersonatingUser?.displayName}
                </Body>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {impersonatableState.impersonatableUsers.map((user) => (
                  <SelectItem key={user.userId} value={user.userId}>
                    {user.displayName}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        )}
        {(hasAdmin || hasManager || canImpersonateUser) && (
          <Select
            value={isImpersonating ? "impersonate" : role.toString()}
            onValueChange={handleRoleChange}
          >
            <SelectTrigger className="border bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {hasAdmin && (
                  <SelectItem value={UserRole.Admin.toString()}>
                    Admin View
                  </SelectItem>
                )}
                {hasManager && (
                  <SelectItem value={UserRole.Manager.toString()}>
                    Manager View
                  </SelectItem>
                )}
                <SelectItem value={UserRole.Employee.toString()}>
                  Employee View
                </SelectItem>
                {canImpersonateUser && (
                  <SelectItem value={"impersonate"}>Delegate View</SelectItem>
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
        )}
        <DropdownMenu onOpenChange={setIsUserMenuOpen} open={isUserMenuOpen}>
          <DropdownMenuTrigger className="flex items-center gap-x-2">
            <span className="relative leading-[0]">
              <Avatar>
                <AvatarFallback>
                  {getInitialsFromDisplayName(displayName ?? "")}
                </AvatarFallback>
              </Avatar>
              {isActionRequired && isAdmin && (
                <Badge
                  variant="destructive"
                  className="absolute -right-2.5 -top-2.5"
                >
                  {1}
                </Badge>
              )}
            </span>
            <CaretDown weight="bold" className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="space-y-4 p-4">
            <div>
              <Body size="md" weight="regular">
                {displayName}
              </Body>
              <Body size="sm" weight="regular" className="mt-1 text-white/70">
                {workEmail}
              </Body>
            </div>
            <div className="space-y-2">
              {!isImpersonating && (
                <Button
                  size="lg"
                  variant="secondary"
                  className="w-full"
                  onClick={onOpenSettings}
                >
                  <span>Settings</span>
                  {isActionRequired && isAdmin && (
                    <Badge variant="destructive" className="ml-2">
                      {1}
                    </Badge>
                  )}
                </Button>
              )}
              <Button
                variant="destructive"
                size="lg"
                className="w-full"
                onClick={onLogout}
              >
                Logout
              </Button>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
